import { Box } from '@mui/material';
import isEqual from 'lodash/isEqual';
import { useSnackbar } from 'notistack';
import React from 'react';

import { DataAPIContext, GET_LIST, GET_ONE, UPDATE } from '../api';
import { ContextStore } from '../store/contextStore';
import { checkAndAddHttpPrefix, useIsWidthUp, validateEmail } from '../utils';
import validURL from '../utils/validators/url';
import { withDefaultTagCategoriesChanges } from './defaultTagCategoriesChanges';
import { handleRefusedSettingsSave } from './refusedSettingsSave';

/** =========================
 *  Helpers & consts (module-level, no React state needed)
 *  ======================= */

export const emptySupportContact = {
  contact: '+1',
  contact_type: 1,
  enabled: true,
  id: null,
  name: '',
};

export const setPath = (obj, key, val) => {
  const root = { ...obj };
  let o = root;
  const [k, ...path] = key.split('.').reverse();
  path.reverse().forEach((c) => {
    const no = Object.prototype.hasOwnProperty.call(o, c) ? { ...o[c] } : {};
    o[c] = no;
    o = no;
  });
  o[k] = val;
  return root;
};

export const normalizeTopForComparison = (value) => {
  if (!value || typeof value !== 'object') {
    return value;
  }
  const normalized = { ...value };
  if (Array.isArray(normalized.default_tag_categories)) {
    normalized.default_tag_categories = normalized.default_tag_categories.map((category) => {
      if (!category || typeof category !== 'object') {
        return category;
      }
      const { idx, is_new, ...rest } = category;
      return rest;
    });
  }
  return normalized;
};

export const TIME_FORMAT = 'HH:mm:ss';

export const EMPTY_ARRAY = [];

export const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** =========================
 *  useGeneralSettings – custom hook
 *  ======================= */
export default function useGeneralSettings(effectiveTopId) {
  const dataAPI = React.useContext(DataAPIContext);
  const resource = 'tour_operator';
  const { tourOperator, updateTourOperator, hasPerm } = React.useContext(ContextStore);
  const { enqueueSnackbar } = useSnackbar();
  const isSmallScreen = !useIsWidthUp('sm');

  // Resolve effective ID: caller may pass a licensee ID, or fall back to parent
  const topId = effectiveTopId || tourOperator.id;

  // ─── State ───────────────────────────────────────────────
  const [top, setTop] = React.useState(tourOperator);
  // The operator as last loaded or saved (JSON): what the form is compared with, and the snapshot the
  // default tag-category change is computed against. Null until the first load of `topId` completes:
  // the bootstrap's operator is the login payload, and for a licensee the parent. Until then the form
  // has no changes, so nothing can be saved. (A licensee switch remounts the page.)
  const [loadedTop, setLoadedTop] = React.useState(null);
  const [dirty, setDirty] = React.useState(false);
  const [syncActive, setSyncActive] = React.useState(false);
  const [tourOperatorSignatures, setTourOperatorSignatures] = React.useState([]);
  const [openChangeHistory, setOpenChangeHistory] = React.useState(false);
  const [rowsWithNoDaysWarning, setRowsWithNoDaysWarning] = React.useState([]);
  const [clientErrors, setClientErrors] = React.useState({
    nonValidUrl: { review: false, photo_sharing: false },
    operatorEmail: false,
    websiteUrl: false,
    bookingUrl: false,
    name: false,
  });

  // Auto-reply isolated field
  const autoReplyRef = React.useRef(null);
  const [autoReplyDirty, setAutoReplyDirty] = React.useState(false);

  // ─── Refs ────────────────────────────────────────────────
  const prevOfficeHoursCountRef = React.useRef(0);

  // ─── Permissions ─────────────────────────────────────────
  const hasWritePerm = hasPerm(resource, 'write');
  const hasReviewsWritePerm = hasPerm('reviews', 'write');

  // ─── Derived review config values ────────────────────────
  const review_widget_type = +top.config?.review_widget_type || 0;
  const review_rating_system = +(top.config?.review_rating_system || 5);
  const review_threshold_default = review_rating_system === 10 ? 8 : 4;
  const review_threshold = +top.config?.reviews_low_high_threshold || review_threshold_default;
  const ratingOptions = React.useMemo(
    () => Array.from({ length: review_rating_system }, (_, index) => ({ value: index + 1 })),
    [review_rating_system]
  );

  // ─── Core state helpers ──────────────────────────────────
  const setTopAndDirty = React.useCallback((updater) => {
    setTop(prev => (typeof updater === 'function' ? updater(prev) : updater));
    setDirty(true);
  }, []);

  const resetDirty = React.useCallback(() => setDirty(false), []);

  // ─── renderRatingValue ───────────────────────────────────
  // NOTE: This returns JSX but is a pure function of review_rating_system.
  // It's kept in the hook because it depends on that derived value.
  const renderRatingValue = React.useCallback(
    (value) => {
      if (review_rating_system === 10) {
        return (
          <Box
            key={value}
            component="span"
            sx={{
              minWidth: 28,
              height: 28,
              px: 1,
              borderRadius: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#007AFF',
              color: '#fff',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {value}
          </Box>
        );
      }
      return <img key={value} src={`/rating-emojis/rating-${value}.svg`} alt={value} style={{ width: '30px', height: '30px' }} />;
    },
    [review_rating_system]
  );

  // ─── Office hours warnings ──────────────────────────────
  const checkAndSetOfficeHoursWarnings = React.useCallback((officeHoursData) => {
    const officeHoursRows = officeHoursData || [];
    const warningIndices = officeHoursRows
      .map((oh, index) => {
        const hasAnyDay = oh.day_of_week_mon || oh.day_of_week_tue || oh.day_of_week_wed ||
          oh.day_of_week_thu || oh.day_of_week_fri || oh.day_of_week_sat || oh.day_of_week_sun;
        return hasAnyDay ? null : index;
      })
      .filter(index => index !== null);
    setRowsWithNoDaysWarning(warningIndices);
    prevOfficeHoursCountRef.current = officeHoursRows.length;
  }, []);

  // ─── Data fetching ──────────────────────────────────────
  // Loads the operator into the form and makes it the loaded snapshot (also the refetch after "Sync with live",
  // which awaits the returned promise). Resolves true when the form now shows the server's operator, false when
  // the load was refused or failed (the form is unchanged then).
  const getTourOperatorUpdateData = React.useCallback(() => {
    // GET_ONE without an id fetches the operator LIST
    if (!topId) {
      return Promise.resolve(false);
    }
    return (async () => {
      try {
        const res = await dataAPI(GET_ONE, resource, { id: topId });
        if (res.statusCode !== 200) {
          return false;
        }
        // Only update bootstrap when editing the parent operator
        if (topId === tourOperator.id) {
          updateTourOperator(res.data);
        }
        setTop(res.data);
        setLoadedTop(JSON.stringify(res.data));
        resetDirty();
        setAutoReplyDirty(false);
        // Check for office hours rows with no days selected on initial load
        checkAndSetOfficeHoursWarnings(res.data?.tour_operator_office_hours_set);
        return true;
      } catch (e) {
        enqueueSnackbar('Error fetching tour operator update data', { variant: 'error' });
        return false;
      }
    })();
  }, [dataAPI, resource, topId, tourOperator.id, updateTourOperator, resetDirty, enqueueSnackbar, checkAndSetOfficeHoursWarnings]);

  const getTourOperatorSignatures = React.useCallback(() => {
    (async () => {
      try {
        const params = topId !== tourOperator.id ? { franchise_to: topId } : {};
        const res = await dataAPI(GET_LIST, 'tour_operator_signature', params);
        if (res.statusCode === 200) {
          let signatures = res.data.map((s, index) => ({ ...s, idx: index }));
          setTourOperatorSignatures(signatures);
        }
      } catch (e) {
        enqueueSnackbar('Error fetching tour operator signatures', { variant: 'error' });
      }
    })();
  }, [dataAPI, enqueueSnackbar, topId, tourOperator.id]);

  // ─── Effects ─────────────────────────────────────────────
  React.useEffect(() => {
    getTourOperatorSignatures();
    getTourOperatorUpdateData();
  }, [getTourOperatorSignatures, getTourOperatorUpdateData]);

  // Initialize the ref with current office hours count
  React.useEffect(() => {
    prevOfficeHoursCountRef.current = top?.tour_operator_office_hours_set?.length || 0;
  }, [top?.tour_operator_office_hours_set?.length]);

  // ─── Validation handlers ────────────────────────────────
  const handleOnBlur = React.useCallback(() => {
    if (!top.name || top.name.length <= 0) {
      setClientErrors(prev => ({ ...prev, name: true }));
    } else {
      setClientErrors(prev => ({ ...prev, name: false }));
    }
  }, [top.name]);

  const handleEmailBlur = React.useCallback((email) => {
    setClientErrors((prev) => ({ ...prev, operatorEmail: !validateEmail(email) }));
  }, []);

  const handleURLOnBlur = React.useCallback((url) => {
    const ok = validURL(url);
    setClientErrors(prev => ({
      ...prev,
      bookingUrl: url === top.payment_url ? !ok : prev.bookingUrl,
      websiteUrl: url === top.url ? !ok : prev.websiteUrl,
    }));
  }, [top.payment_url, top.url]);

  // ─── Default tag categories changed elsewhere (409) ─────
  // The save was refused as a whole. The reloaded list becomes the new snapshot, keeping every
  // other edit in the form (still unsaved, so Save stays enabled for them); the user re-applies
  // their change to the defaults on the fresh rows.
  const adoptReloadedDefaults = (operator) => {
    const fresh = operator.default_tag_categories;
    setTop(prev => ({ ...prev, default_tag_categories: fresh }));
    setLoadedTop(prev => JSON.stringify({ ...JSON.parse(prev), default_tag_categories: fresh }));
    if (topId === tourOperator.id) {
      updateTourOperator(operator);
    }
  };

  // ─── Save / Cancel ──────────────────────────────────────
  // saveChanges now accepts an optional payload override
  // NOTE: intentionally NOT wrapped in useCallback — matches original General.js behavior
  const saveChanges = (updateFlag, updated_review_data, payloadOverride = null) => {
    if (loadedTop === null) {
      return; // not loaded yet: there is no snapshot to compute the change against
    }
    let top_data = payloadOverride ? payloadOverride : { ...top };

    if (updateFlag === 'ReviewDelete') {
      top_data.review = null;
    }
    if (updateFlag === 'ReviewUpdate') {
      top_data.review = updated_review_data;
    }

    // sdtc WP-C: never the default tag-category list itself, only its change against the list
    // this page loaded (row ids, `from` = the loaded values), and only when there is one.
    const body = withDefaultTagCategoriesChanges(top_data, JSON.parse(loadedTop)?.default_tag_categories);

    setSyncActive(true);
    dataAPI(UPDATE, resource, { id: topId, data: body })
      .then(async (res) => {
        // A refused save resolves with the error body. Keep the user's edits (onSave already
        // put them in `top`; loadedTop is unchanged, so Save stays enabled) and say why.
        const refused = await handleRefusedSettingsSave(res, {
          dataAPI,
          operatorId: topId,
          rows: top_data.default_tag_categories,
          enqueueSnackbar,
          onDefaultsReloaded: adoptReloadedDefaults,
        });
        if (refused) {
          return;
        }
        if (!updateFlag) {
          enqueueSnackbar('Settings saved', { variant: 'success' });
        }
        setTop(res.data);
        setLoadedTop(JSON.stringify(res.data));
        if (topId === tourOperator.id) {
          updateTourOperator(res.data);
        }
        resetDirty();
        setAutoReplyDirty(false);

        // Check for office hours rows with no days selected and set warnings
        checkAndSetOfficeHoursWarnings(res.data?.tour_operator_office_hours_set);

        // Sync notification sound settings to IndexedDB for service worker
        if (res.data?.config?.background_dashboard_chat_notification_sound_enabled !== undefined || res.data?.config?.foreground_dashboard_chat_notification_sound_enabled !== undefined) {
          try {
            const { set: IndexedDB_set } = await import('idb-keyval');
            if (res.data.config.background_dashboard_chat_notification_sound_enabled !== undefined) {
              IndexedDB_set('notificationSoundEnabledBackground', !!res.data.config.background_dashboard_chat_notification_sound_enabled);
            }
            if (res.data.config.foreground_dashboard_chat_notification_sound_enabled !== undefined) {
              IndexedDB_set('notificationSoundEnabledForeground', !!res.data.config.foreground_dashboard_chat_notification_sound_enabled);
            }
          } catch (err) {
            console.warn('Failed to sync notification sound settings to IndexedDB:', err);
          }
        }
      })
      .catch((err) => {
        enqueueSnackbar('Error saving settings', { variant: 'error' });
        console.log('error saving settings', err);
      })
      .finally(() => {
        setSyncActive(false);
      });
  };

  // ─── Field change handlers ──────────────────────────────
  const onChange = React.useCallback((ev) => {
    const { name, value } = ev.target;
    setTopAndDirty(prev => ({ ...prev, [name]: value }));
  }, [setTopAndDirty]);

  const onSubfieldChange = React.useCallback((field, value) => {
    setTopAndDirty(prev => setPath(prev, field, value));
  }, [setTopAndDirty]);

  // ─── Office hours ───────────────────────────────────────
  const onTourOperatorOfficeHoursChange = React.useCallback((rows) => {
    const currentCount = rows.length;
    const prevCount = prevOfficeHoursCountRef.current;

    // If rows were deleted, clear warnings (they'll be recalculated on next save)
    if (currentCount < prevCount) {
      setRowsWithNoDaysWarning([]);
    }
    prevOfficeHoursCountRef.current = currentCount;

    setTopAndDirty(prev => ({
      ...prev,
      tour_operator_office_hours_set: rows.map((row) => ({
        office_hours_start_time: row.startTime ? row.startTime.format(TIME_FORMAT) : null,
        office_hours_end_time: row.endTime ? row.endTime.format(TIME_FORMAT) : null,
        timezone: row.timezone,
        day_of_week_sun: row.selectedDays.includes('Sun'),
        day_of_week_mon: row.selectedDays.includes('Mon'),
        day_of_week_tue: row.selectedDays.includes('Tue'),
        day_of_week_wed: row.selectedDays.includes('Wed'),
        day_of_week_thu: row.selectedDays.includes('Thu'),
        day_of_week_fri: row.selectedDays.includes('Fri'),
        day_of_week_sat: row.selectedDays.includes('Sat'),
      })),
    }));

    // Clear warning for rows that now have days selected
    setRowsWithNoDaysWarning(prev => {
      const updated = prev.filter(index => {
        const row = rows[index];
        return row && row.selectedDays.length === 0;
      });
      return updated;
    });
  }, [setTopAndDirty]);

  // ─── Office hours validation ────────────────────────────
  // All office-hours rows must have start, end, and timezone
  const officeHoursValid = React.useMemo(() => {
    const rows = top?.tour_operator_office_hours_set || [];
    return rows.every(r =>
      !!r?.office_hours_start_time &&
      !!r?.office_hours_end_time &&
      !!r?.timezone
    );
  }, [top?.tour_operator_office_hours_set]);

  // ─── Dirty / change tracking ────────────────────────────
  const loadedTopObject = React.useMemo(() => (loadedTop === null ? null : JSON.parse(loadedTop)), [loadedTop]);
  const comparableTop = React.useMemo(() => normalizeTopForComparison(top), [top]);
  const comparableLoadedTop = React.useMemo(
    () => normalizeTopForComparison(loadedTopObject),
    [loadedTopObject]
  );
  // Before the first load nothing counts as a change (see loadedTop).
  const hasTopChanges = React.useMemo(
    () => loadedTopObject !== null && !isEqual(comparableTop, comparableLoadedTop),
    [comparableTop, comparableLoadedTop, loadedTopObject]
  );
  const hasFormChanges = loadedTopObject !== null && (hasTopChanges || autoReplyDirty);

  React.useEffect(() => {
    setDirty(hasTopChanges);
  }, [hasTopChanges]);

  const officeHoursChanged = React.useMemo(() => {
    const currentRows = top?.tour_operator_office_hours_set || [];
    const loadedRows = loadedTopObject?.tour_operator_office_hours_set || [];
    return !isEqual(currentRows, loadedRows);
  }, [top?.tour_operator_office_hours_set, loadedTopObject]);

  // ─── Support contact handlers ───────────────────────────
  const onSupportContactChange = React.useCallback((data, idx) => {
    setTopAndDirty(prev => ({
      ...prev,
      support_contacts: (prev.support_contacts || []).map((c, i) => (i === idx ? data : c)),
    }));
  }, [setTopAndDirty]);

  const onSupportContactDelete = React.useCallback((idx) => {
    setTopAndDirty(prev => ({
      ...prev,
      support_contacts: (prev.support_contacts || []).filter((_, i) => i !== idx),
    }));
  }, [setTopAndDirty]);

  const onSupportContactAdd = React.useCallback(() => {
    setTopAndDirty(prev => ({
      ...prev,
      support_contacts: [...(prev.support_contacts || []), emptySupportContact],
    }));
  }, [setTopAndDirty]);

  // ─── Localized text set handler ─────────────────────────
  const onLocalizedTextSetChange = React.useCallback((ev) => {
    const { name, value } = ev.target;
    setTopAndDirty(prev => ({ ...prev, [name]: value }));
  }, [setTopAndDirty]);

  // ─── Trip default settings handlers ─────────────────────
  const onDefaultLocationSharingChange = React.useCallback(
    (e) => onSubfieldChange('config.defaults.location_sharing', +e.target.value),
    [onSubfieldChange]
  );
  const onDefaultMyTripsDisplayChange = React.useCallback(
    (e) => onSubfieldChange('config.defaults.mytrips_display', e.target.checked),
    [onSubfieldChange]
  );
  const onDefaultHasGroupChatChange = React.useCallback(
    (e) => onSubfieldChange('config.defaults.has_group_chat', e.target.checked),
    [onSubfieldChange]
  );
  const onSetDefaultsAssociatedTagCategories = React.useCallback(
    (list) => setTopAndDirty((prev) => ({ ...prev, default_tag_categories: list })),
    [setTopAndDirty]
  );

  // ─── Save & Cancel ──────────────────────────────────────
  const onSave = React.useCallback(() => {
    // 1) force-commit the isolated field into state AND capture its draft
    autoReplyRef.current?.flush?.();
    const draft = autoReplyRef.current?.getDraft?.() ?? (top.outside_office_hours_auto_reply?.auto_reply_message_text || "");

    // 2) build payload override with freshest draft (no race with setState)
    const payload = {
      ...top,
      payment_url: checkAndAddHttpPrefix(top.payment_url),
      url: checkAndAddHttpPrefix(top.url),
      outside_office_hours_auto_reply: {
        ...(top.outside_office_hours_auto_reply || {}),
        auto_reply_message_text: draft,
      },
      // Ensure config is properly included (setPath creates new objects, so we need to preserve the full config)
      config: {
        ...(top.config || {}),
      },
    };

    // 3) optimistic local update (keeps UI consistent), then save with override
    setTop(payload);
    if (hasFormChanges) {
      saveChanges(undefined, undefined, payload);
    }
    // loadedTop: saveChanges computes the default tag-category change against it, so a stale
    // closure would send a change from an outdated snapshot (and 409 forever after a reload).
  }, [hasFormChanges, top, loadedTop]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCancel = React.useCallback(() => {
    if (loadedTop === null) {
      return;
    }
    setTop(JSON.parse(loadedTop));
    resetDirty();
    setAutoReplyDirty(false);
  }, [loadedTop, resetDirty]);

  // ─── Signature handlers ─────────────────────────────────
  const handleSignatureCreate = React.useCallback(() => {
    setTourOperatorSignatures((prev) => [...prev, { idx: prev.length, name: '', text: '', created: true }]);
  }, []);

  const updateTourOperatorSignatures = React.useCallback((idx, update) => {
    if (!update) {
      setTourOperatorSignatures((prev) => {
        let signatures = prev.filter((signature) => signature.idx !== idx);
        signatures = signatures.map((s, i) => ({ ...s, idx: i }));
        return signatures;
      });
    } else {
      setTourOperatorSignatures((prev) => {
        let signatures = [...prev.filter((signature) => signature.idx !== idx), update];
        signatures = signatures.map((s, i) => ({ ...s, idx: i }));
        return signatures;
      });
    }
  }, []);

  // ─── Review handlers ────────────────────────────────────
  const handleReviewChange = React.useCallback((ev) => {
    const { name, value } = ev.target;
    if (name === 'review_name' || name === 'review_link') {
      if (value === '' && (!top.review?.url || top.review?.url === '')) {
        setTopAndDirty(prev => ({ ...prev, review: null }));
        return;
      }
      if (value === '' && (!top.review?.name || top.review?.name === '')) {
        setTopAndDirty(prev => ({ ...prev, review: null }));
        return;
      }
      if (name.includes('name')) {
        setTopAndDirty(prev => ({ ...prev, review: { ...prev.review, name: value } }));
      } else {
        setTopAndDirty(prev => ({ ...prev, review: { ...prev.review, url: value } }));
      }
    } else {
      if (value === '' && (!top.photo_sharing?.url || top.photo_sharing?.url === '')) {
        setTopAndDirty(prev => ({ ...prev, photo_sharing: null }));
        return;
      }
      if (value === '' && (!top.photo_sharing?.name || top.photo_sharing?.name === '')) {
        setTopAndDirty(prev => ({ ...prev, photo_sharing: null }));
        return;
      }
      if (name.includes('name')) {
        setTopAndDirty(prev => ({ ...prev, photo_sharing: { ...prev.photo_sharing, name: value } }));
      } else {
        setTopAndDirty(prev => ({ ...prev, photo_sharing: { ...prev.photo_sharing, url: value } }));
      }
    }
  }, [setTopAndDirty, top.review, top.photo_sharing]);

  const checkValidURL = React.useCallback(() => {
    let errors = {
      review: !validURL(top.review?.url),
      photo_sharing: !validURL(top.photo_sharing?.url),
    };
    setClientErrors((prev) => ({ ...prev, nonValidUrl: errors }));
  }, [top.review?.url, top.photo_sharing?.url]);

  const onReviewLowHighThresholdChange = React.useCallback((event) => {
    const v = +event.target.value || review_threshold_default;
    setTopAndDirty(prev => ({
      ...prev,
      config: {
        ...prev.config,
        reviews_low_high_threshold: v,
      },
    }));
  }, [review_threshold_default, setTopAndDirty]);

  const onReviewRatingSystemChange = React.useCallback((ev) => {
    const reviewRatingSystem = +ev.target.value;
    const nextThreshold = reviewRatingSystem === 10 ? 8 : 4;
    setTopAndDirty(prev => ({
      ...prev,
      config: {
        ...prev.config,
        review_rating_system: reviewRatingSystem,
        reviews_low_high_threshold: nextThreshold,
      },
    }));
  }, [setTopAndDirty]);

  const onReviewWidgetTypeChange = React.useCallback((ev) => {
    const v = ev.target.value;
    setTopAndDirty(prev => {
      const cf = prev.config || {};
      return { ...prev, config: { ...cf, [ev.target.name]: v } };
    });
  }, [setTopAndDirty]);

  // ─── System tags handler ────────────────────────────────
  const onSystemTagsChange = React.useCallback((ev, stags) => {
    setTopAndDirty(prevState => ({ ...prevState, tags: stags }));
  }, [setTopAndDirty]);

  // ─── Return everything the UI needs ─────────────────────
  return {
    // Context values
    resource,
    hasPerm,
    isSmallScreen,

    // State
    top,
    setTop,
    dirty,
    setDirty,
    syncActive,
    loadedTop,
    tourOperatorSignatures,
    openChangeHistory,
    setOpenChangeHistory,
    clientErrors,
    setClientErrors,
    autoReplyDirty,
    setAutoReplyDirty,
    rowsWithNoDaysWarning,

    // Refs
    autoReplyRef,
    prevOfficeHoursCountRef,

    // Permissions
    hasWritePerm,
    hasReviewsWritePerm,

    // Derived review config
    review_widget_type,
    review_rating_system,
    review_threshold_default,
    review_threshold,
    ratingOptions,
    renderRatingValue,

    // Core state helpers
    setTopAndDirty,
    resetDirty,

    // Validation
    officeHoursValid,
    officeHoursChanged,
    hasTopChanges,
    hasFormChanges,

    // Validation handlers
    handleOnBlur,
    handleEmailBlur,
    handleURLOnBlur,
    checkValidURL,

    // Data fetching
    getTourOperatorUpdateData,
    getTourOperatorSignatures,

    // Save / Cancel
    saveChanges,
    onSave,
    onCancel,

    // Field change handlers
    onChange,
    onSubfieldChange,
    onLocalizedTextSetChange,

    // Office hours
    onTourOperatorOfficeHoursChange,
    checkAndSetOfficeHoursWarnings,

    // Support contacts
    onSupportContactChange,
    onSupportContactDelete,
    onSupportContactAdd,

    // Trip defaults
    onDefaultLocationSharingChange,
    onDefaultMyTripsDisplayChange,
    onDefaultHasGroupChatChange,
    onSetDefaultsAssociatedTagCategories,

    // Signatures
    handleSignatureCreate,
    updateTourOperatorSignatures,

    // Reviews
    handleReviewChange,
    onReviewLowHighThresholdChange,
    onReviewRatingSystemChange,
    onReviewWidgetTypeChange,

    // System tags
    onSystemTagsChange,
  };
}
